<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div class="action-container justify-content-end">
        <el-button
          :disabled="isLoading"
          :loading="isSaving"
          class="save-button"
          size="mini"
          type="danger"
          icon="el-icon-upload2"
          @click="handleSave"
        >{{ $t('button.save') }}</el-button>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <generic-form
        ref="form"
        :attributes="formAttributes"
        :form="form"
        :form-template="formTemplate"
        class="pt-3"
      />
    </div>
  </sticky-header-layout>
</template>

<script>
import ConfigService from '../../api/config'
import GenericForm from '../../components/form/Form.vue'
import { FormRules } from '../../utils/form'

export default {
  name: 'ConfigPage',
  components: {
    GenericForm,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const formTemplate = [
      {
        key: 'stickyHomeIdTitle',
        isTitle: true,
        title: this.$t('config.sticky_home_ids'),
      },
      {
        key: 'sticky_home_ids',
        isInput: true,
        custom: {
          autosize: { minRows: 5, maxRows: 12 },
          type: 'textarea',
        },
        title: ' ',
        description: this.$t('config.sticky_home_ids_desc'),
        trim: true,
        rules: [FormRules.idList],
      },
      {
        key: 'ignoreUsersTitle',
        isTitle: true,
        title: this.$t('config.ignore_users'),
      },
      {
        key: 'ignored_ids',
        isInput: true,
        custom: {
          autosize: { minRows: 5, maxRows: 12 },
          type: 'textarea',
        },
        title: ' ',
        description: this.$t('config.ignore_users_desc'),
        trim: true,
        rules: [FormRules.idList],
      },
    ]

    return {
      isEdited: false,
      isLoading: false,
      isSaving: false,
      formTemplate,
      form: {
        sticky_home_ids: '',
        ignored_ids: '',
      },
    }
  },
  computed: {
    formAttributes() {
      return {
        disabled: this.isSaving,
        labelWidth: '4px',
      }
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
  },
  mounted() {
    this.getDetail(this.id)
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }
    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    handleSave() {
      this.$refs.form.$refs.form.validate().then(() => {
        this.editConfig()
      }).catch((error) => {
        console.error(error);
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.$message(this.$t('success.savedSuccessfully'));
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    goToDashbard() {
      this.$router.push({ path: '/dashboard' })
    },
    async getDetail() {
      this.isLoading = true
      try {
        const { data } = await ConfigService.getConfig()
        this.form = data
        this.$nextTick(() => {
          this.isEdited = false
        })
      } catch (error) {
        console.error(error);
        this.$alert(this.$t('error.configNotFound'), this.$t('common.error'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        }).then(() => {
          this.goToDashbard()
        })
      } finally {
        this.isLoading = false
      }
    },
    async editConfig() {
      this.isSaving = true
      try {
        await ConfigService.update(this.form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";

.config-desc {
  font-size: 0.875rem;
}
</style>
